import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Anchor, Box, Button, Container, Group, Loader, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { IconCamera, IconPhoto } from '@tabler/icons-react';
import { ScanStage, ScanStageHandle } from '../components/scan/ScanStage';
import { ScanSheet, ScanSheetHandle } from '../components/scan/ScanSheet';
import { beefLines, YakinikuView, yakinikuOrder } from '../components/scan/YakinikuView';
import { notADish, ScanMenuView } from '../components/scan/ScanMenuView';
import { InfoState, ScanItemInfo, ScanItemRef, ScanLine } from '../components/scan/scanTypes';
import { analyzeLine, askAboutItem, getKnownItem, scanErrorMessage, scanLines, scanPhoto } from '../services/scanApi';
import { preparePhoto } from '../utils/scanImage';
import { findAll, menuAnimalOf } from '../utils/guide';
import classes from './MenuScan.module.css';

const SAMPLES: Record<string, string> = { 'test-menu': 'A yakiniku menu' };

interface Status { text: string; error?: boolean; busy?: boolean }

const MENU_SAMPLE_SIZE = 40;

/** True if a line has no Japanese script and at least one Latin letter — already English, so nothing to translate. */
const isEnglishLine = (t: string) => !/[぀-ヿ㐀-鿿]/.test(t) && /[a-z]/i.test(t);

/** Lines printed around line i, for the AI's context (descriptions, prices). */
const nearbyText = (lines: ScanLine[], i: number) =>
  lines.slice(Math.max(0, i - 3), i + 4).filter((l, k) => k !== Math.min(3, i) && !l.dropped).map((l) => l.t);

/** Lines spread across the whole menu, so the AI knows what kind of place it is. */
function menuSampleText(all: ScanLine[]): string[] {
  const lines = all.filter((l) => !l.dropped);
  const step = Math.max(1, lines.length / MENU_SAMPLE_SIZE);
  const out: string[] = [];
  for (let k = 0; k < lines.length && out.length < MENU_SAMPLE_SIZE; k += step) out.push(lines[Math.floor(k)].t);
  return out;
}

/**
 * Experimental: photograph a menu, and every ingredient (or beef cut) it names
 * lights up. Tap one to see a picture and what it is; beef cuts show the cow
 * with the section lit. Built on the /scan page: same reading by Cloud Vision,
 * but lines are matched in the browser against the ingredient and beef-cut
 * guides instead of against stored menu items.
 */
const TRANSLATE_AT_ONCE = 5;

interface MenuScanIngredientProps {
  /** Once the lookup of known dishes is done, explain every other line in the background, a few at a time */
  autoTranslate?: boolean;
  /** Offer a yakiniku view: just the beef, the meat on a tenderness × marbling chart and the offal listed apart */
  yakiniku?: boolean;
  /** v3: the photo keeps its English titles hidden; a line's title appears once the diner taps it */
  revealOnTap?: boolean;
}

/** The photo with titles on it; the menu as a list (v2); just the beef (yakiniku) */
type View = 'photo' | 'menu' | 'yakiniku';

/** Viewport px the floating view switch covers at the top */
const SWITCH_INSET = 52;

const MenuScanIngredient: React.FC<MenuScanIngredientProps> = ({ autoTranslate = false, yakiniku = false, revealOnTap = false }) => {
  const [searchParams] = useSearchParams();
  const [photoMode, setPhotoMode] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [rawLines, setRawLines] = useState<ScanLine[]>([]);
  const [scanning, setScanning] = useState(false);
  const [status, setStatus] = useState<Status>({ text: '' });
  const [selected, setSelected] = useState(-1);
  const [view, setView] = useState<View>('photo');

  const run = useRef(0);
  // Bumped for each scan that has lines; starts the background translation
  const [scanKey, setScanKey] = useState(0);
  const pushed = useRef(false);
  const stageRef = useRef<ScanStageHandle>(null);
  const sheetOpenRef = useRef(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [infos, setInfos] = useState<Record<number, InfoState>>({});
  const infosRef = useRef(infos);
  infosRef.current = infos;
  // The English title over each line's box: the stored dish's name, or the AI's once a tap has explained the line
  const lines = useMemo<ScanLine[]>(() => rawLines.map((l, i) => {
    const info = infos[i];
    const explained = info?.status === 'ready' && info.info.isMenuItem !== false ? info.info.item.props?.translations?.en?.name?.text : undefined;
    // Already English: grey it out like a dropped line and skip translation
    const dropped = l.dropped || (autoTranslate && !l.match && isEnglishLine(l.t));
    return { ...l, dropped, label: dropped ? undefined : l.match?.nameEn || explained || undefined };
  }), [rawLines, infos, autoTranslate]);
  const linesRef = useRef<ScanLine[]>([]);
  linesRef.current = lines;
  // v3: the lines tapped so far; only these show their English title on the photo
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set());
  const stageLines = useMemo(
    () => (revealOnTap ? lines.map((l, i) => (l.label && !revealed.has(i) ? { ...l, label: undefined, ready: true } : l)) : lines),
    [lines, revealOnTap, revealed],
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const menuAnimal = useMemo(() => menuAnimalOf(rawLines.filter((l) => !l.dropped).map((l) => l.t)), [rawLines]);
  const beef = useMemo(() => (yakiniku ? beefLines(rawLines, menuAnimal) : []), [yakiniku, rawLines, menuAnimal]);
  const yakinikuView = view === 'yakiniku' && beef.length > 0;
  const menuView = view === 'menu' && autoTranslate;
  // Printed in English already: dropped (nothing to explain), but still part of the menu
  const english = useMemo(() => new Set(lines.flatMap((l, i) => (l.dropped && !rawLines[i]?.dropped ? [i] : []))), [lines, rawLines]);
  // The lines the sheet's arrows step through: every line, or the beef as the yakiniku view lists it. Lines the AI
  // found aren't dishes (headings, notes) are skipped, except the one open now, so its arrows still work.
  const order = useMemo(
    () => (yakinikuView ? yakinikuOrder(beef) : lines.flatMap((l, i) => (l.dropped ? [] : [i]))).filter((i) => i === selected || !notADish(infos[i])),
    [yakinikuView, beef, lines, infos, selected],
  );
  const orderRef = useRef(order);
  orderRef.current = order;

  const sheetRef = useRef<ScanSheetHandle>(null);
  const coverTop = useCallback(() => sheetRef.current?.coverTop() ?? window.innerHeight, []);
  const closeSheet = useCallback(() => {
    sheetOpenRef.current = false;
    setSheetOpen(false);
    setSelected(-1);
    stageRef.current?.release();
  }, []);

  const enterPhotoMode = useCallback(() => {
    setPhotoMode(true);
    if (!pushed.current) {
      try { window.history.pushState({ scan: true }, ''); pushed.current = true; } catch (e) { /* ignore */ }
    }
    window.scrollTo(0, 0);
  }, []);

  const startOver = useCallback(() => {
    run.current++;
    closeSheet();
    setPhotoMode(false);
    setView('photo');
    setRawLines([]);
    setInfos({});
    setRevealed(new Set());
    setScanning(false);
    window.scrollTo(0, 0);
  }, [closeSheet]);

  useEffect(() => {
    const onPop = () => {
      if (sheetOpenRef.current) {
        closeSheet();
        try { window.history.pushState({ scan: true }, ''); } catch (e) { /* ignore */ }
        return;
      }
      pushed.current = false;
      startOver();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [closeSheet, startOver]);

  const showLines = useCallback((next: ScanLine[]) => {
    if (next.some((l) => l.jev !== undefined)) {
      console.groupCollapsed(`[scan-ingredient] Jev: ${next.filter((l) => l.dropped).length} of ${next.length} lines dropped`);
      console.table(next.map((l, i) => ({ '#': i, text: l.t, jev: l.jev == null ? 'n/a' : Number(l.jev.toFixed(2)), result: l.dropped ? 'DROP' : 'keep' })));
      console.groupEnd();
    }
    setRawLines(next);
    setInfos({});
    setRevealed(new Set());
    setScanning(false);
    if (next.length) setScanKey((k) => k + 1);
    setStatus(next.length ? { text: 'Tap anything on the menu to see what it is.' } : { text: "Couldn't find any text in this photo. Try again closer, with the menu flat and in focus.", error: true });
  }, []);

  const beginReading = useCallback((url: string) => {
    closeSheet();
    enterPhotoMode();
    setPhotoUrl((old) => {
      if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
      return url;
    });
    setView('photo');
    setRawLines([]);
    setInfos({});
    setRevealed(new Set());
    setScanning(true);
    setStatus({ text: 'Reading the menu…', busy: true });
  }, [closeSheet, enterPhotoMode]);

  const readPhoto = useCallback(async (file: File) => {
    const id = ++run.current;
    closeSheet();
    enterPhotoMode();
    setStatus({ text: 'Getting the photo ready…', busy: true });
    let prepared;
    try {
      prepared = await preparePhoto(file);
    } catch (e) {
      if (id === run.current) setStatus({ text: "That file didn't open as a photo. Try another.", error: true });
      return;
    }
    if (id !== run.current) return;
    beginReading(URL.createObjectURL(prepared.blob));
    try {
      const result = await scanPhoto(prepared.blob, true, true, true);
      if (id === run.current) showLines(result.lines);
    } catch (e) {
      if (id !== run.current) return;
      setScanning(false);
      setStatus({ text: `Couldn't read the menu: ${scanErrorMessage(e, 'something went wrong.')}`, error: true });
    }
  }, [beginReading, closeSheet, enterPhotoMode, showLines]);

  const openSample = useCallback(async (sample: string) => {
    const id = ++run.current;
    beginReading(`/scan-samples/${sample}.jpg`);
    try {
      const res = await fetch(`/scan-samples/${sample}.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: { engine: string; items: ScanLine[] } = await res.json();
      const result = await scanLines(data.items, data.engine, true, true);
      if (id === run.current) showLines(result.lines);
    } catch (e) {
      if (id !== run.current) return;
      setScanning(false);
      setStatus({ text: `Couldn't load the sample: ${scanErrorMessage(e, (e as Error).message)}`, error: true });
    }
  }, [beginReading, showLines]);

  // ?sample=test-menu opens a sample straight away
  const sampleParam = searchParams.get('sample');
  useEffect(() => {
    if (sampleParam && SAMPLES[sampleParam]) openSample(sampleParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleParam]);

  /**
   * What a tapped line is: the stored dish when the shared menu has it, else an
   * AI explanation, which the server keeps on the line's item. The line then counts as known.
   */
  const loadInfo = useCallback(async (i: number) => {
    const id = run.current;
    const all = linesRef.current;
    const line = all[i];
    if (!line) return;
    setInfos((m) => ({ ...m, [i]: { status: 'loading' } }));
    let info: ScanItemInfo;
    try {
      info = line.match
        ? await getKnownItem(line.match.id)
        : await analyzeLine(line.t, nearbyText(all, i), menuSampleText(all), line.itemId);
      if (id !== run.current) return;
      setInfos((m) => ({ ...m, [i]: { status: 'ready', info } }));
    } catch (e) {
      if (id === run.current) setInfos((m) => ({ ...m, [i]: { status: 'error', error: scanErrorMessage(e, 'Could not look this up. Try again.') } }));
      return;
    }
    // Saved on the server: from now on this line shows its English name on the photo
    const nameEn = info.item.props?.translations?.en?.name?.text;
    if (!line.match && line.itemId && info.isMenuItem !== false && nameEn) {
      const match = { id: line.itemId, name: line.t, nameEn, restaurantName: '', menuId: null, count: 1, how: 'exact' as const };
      setRawLines((rows) => rows.map((r, k) => (k === i ? { ...r, match } : r)));
    }
  }, []);

  // Stop any background work if the page goes away
  useEffect(() => () => { run.current++; }, []);

  /**
   * v2: once the lines are looked up, explain every line that has no English yet,
   * TRANSLATE_AT_ONCE at a time, in reading order. Each result puts its title on the photo.
   * A line the diner has already tapped is skipped; a new photo stops the rest.
   */
  useEffect(() => {
    if (!autoTranslate || scanKey === 0) return;
    const id = run.current;
    const todo = linesRef.current.map((l, i) => i).filter((i) => !linesRef.current[i].dropped && !linesRef.current[i].match);
    if (!todo.length) return;
    let next = 0, done = 0;
    const progress = () => setStatus({ text: `Translating the menu… ${done} of ${todo.length}`, busy: true });
    progress();
    const worker = async () => {
      while (id === run.current && next < todo.length) {
        const i = todo[next++];
        if (!infosRef.current[i]) await loadInfo(i);
        done++;
        if (id === run.current && !sheetOpenRef.current) progress();
      }
    };
    Promise.all(Array.from({ length: Math.min(TRANSLATE_AT_ONCE, todo.length) }, worker)).then(() => {
      if (id === run.current) setStatus({ text: 'Tap anything on the menu to see what it is.' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanKey]);

  const askQuestion = useCallback((info: ScanItemInfo, question: string) => {
    const ref: ScanItemRef = { item: info.item };
    return askAboutItem(ref, question);
  }, []);

  /** The next line in `order`, going in `dir` from i (-1 if none). */
  const step = (i: number, dir: 1 | -1) => {
    const at = order.indexOf(i);
    return at < 0 ? -1 : order[at + dir] ?? -1;
  };

  const select = useCallback((i: number, fromList = false) => {
    if (linesRef.current[i]?.dropped) return;
    setSelected(i);
    if (revealOnTap) setRevealed((r) => (r.has(i) ? r : new Set(r).add(i)));
    sheetOpenRef.current = true;
    setSheetOpen(true);
    const state = infosRef.current[i];
    if (!state || state.status === 'error') loadInfo(i);
    // The sheet overlays the photo: v2 moves the line into the space above it
    stageRef.current?.reveal(i, fromList, autoTranslate ? coverTop : undefined);
  }, [autoTranslate, coverTop, loadInfo, revealOnTap]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as Element).closest('input, textarea')) return;
      if (e.key === 'Escape' && sheetOpenRef.current) closeSheet();
      if (selected < 0) return;
      const move = (dir: 1 | -1) => {
        const list = orderRef.current, at = list.indexOf(selected);
        const k = at < 0 ? -1 : list[at + dir] ?? -1;
        if (k >= 0) select(k, true);
      };
      if (e.key === 'ArrowRight') move(1);
      if (e.key === 'ArrowLeft') move(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeSheet, select, selected]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) readPhoto(file);
  };
  const line = selected >= 0 ? lines[selected] : null;
  // Once the photo is read: the photo, the menu as a list (v2), and the beef when the menu has any
  const views = rawLines.length ? [
    { value: 'photo', label: 'Photo' },
    ...(autoTranslate ? [{ value: 'menu', label: 'Menu' }] : []),
    ...(beef.length ? [{ value: 'yakiniku', label: 'Yakiniku' }] : []),
  ] : [];

  return (
    <Box className={classes.page}>
      <Container size={720} px="md">
        {!photoMode && (
          <Stack gap="lg" className={classes.intro}>
            <Stack gap="xs">
              <Text size="xs" fw={700} c="orange" tt="uppercase">Experimental</Text>
              <Title order={1} className={classes.title}>What's in this dish?</Title>
              <Text size="lg" c="dimmed">
                Take a photo of a menu. Every ingredient and beef cut it names lights up; tap one for a picture and what it is.
              </Text>
            </Stack>
            <Stack gap="sm">
              <Button size="lg" radius="xl" leftSection={<IconCamera size={22} />} onClick={() => cameraInput.current?.click()}>
                Photograph a menu
              </Button>
              <Group gap="lg">
                <Button variant="subtle" leftSection={<IconPhoto size={18} />} onClick={() => fileInput.current?.click()} px={0}>
                  Choose a photo
                </Button>
                {Object.entries(SAMPLES).map(([id, label]) => (
                  <Anchor key={id} component="button" type="button" size="sm" onClick={() => openSample(id)}>
                    or try {label.toLowerCase()}
                  </Anchor>
                ))}
              </Group>
              <Text size="xs" c="dimmed">
                Your photo is read by Google Cloud Vision. Ingredients: 300 vegetables, fruit, fish and seafood, with Japan's
                seasons. Beef cuts: 45 cuts and 21 kinds of offal, with ratings and where they sit on the cow.
              </Text>
            </Stack>
          </Stack>
        )}

        {photoMode && (
          <Stack gap={0}>
            {/* Floats over the top of the photo (and stays there over the lists), taking no room */}
            {views.length > 1 && (
              <div className={classes.viewSwitch}>
                <SegmentedControl
                  value={view}
                  onChange={(v) => { closeSheet(); setView(v as View); window.scrollTo(0, 0); }}
                  data={views}
                  radius="xl"
                  size="sm"
                  className={classes.viewSwitchControl}
                  aria-label="Show the menu photo, the menu as a list, or just its beef cuts"
                />
              </div>
            )}
            {yakinikuView ? (
              <Box pt={views.length > 1 ? SWITCH_INSET - 8 : 0}>
                <YakinikuView items={beef} selected={selected} onPick={(i) => select(i, true)} />
              </Box>
            ) : menuView ? (
              <Box pt={SWITCH_INSET}>
                <ScanMenuView
                  lines={lines}
                  infos={infos}
                  english={english}
                  selected={selected}
                  onPick={(i) => select(i, true)}
                  sheetOpen={sheetOpen}
                  coverTop={coverTop}
                  topInset={SWITCH_INSET}
                />
              </Box>
            ) : (
              <ScanStage
                ref={stageRef}
                photoUrl={photoUrl}
                lines={stageLines}
                selected={selected}
                scanning={scanning}
                onTapLine={(i) => select(i)}
                onTapEmpty={() => { if (sheetOpenRef.current) closeSheet(); }}
                onRetake={() => cameraInput.current?.click()}
                tall
                topInset={views.length > 1 ? SWITCH_INSET : 0}
                overlay={selected < 0 && status.text ? (
                  <Group gap={8} wrap="nowrap" role="status" aria-live="polite">
                    {status.busy && <Loader size={14} color="white" />}
                    <span style={status.error ? { fontWeight: 600, color: '#ffa8a8' } : undefined}>{status.text}</span>
                  </Group>
                ) : null}
              />
            )}
          </Stack>
        )}
      </Container>

      {(() => {
        const common = {
          position: { index: order.indexOf(selected) + 1, total: order.length },
          prev: selected >= 0 && step(selected, -1) >= 0 ? { label: lines[step(selected, -1)].t, onClick: () => select(step(selected, -1), true) } : undefined,
          next: selected >= 0 && step(selected, 1) >= 0 ? { label: lines[step(selected, 1)].t, onClick: () => select(step(selected, 1), true) } : undefined,
          onClose: closeSheet,
        };
        // The guides don't say what a line is; they add notes about the cuts and ingredients its text mentions
        const guideHits = line ? findAll(line.t) : [];
        return (
          <>
            <ScanSheet
              ref={sheetRef}
              opened={sheetOpen}
              index={selected}
              line={line}
              state={selected >= 0 ? infos[selected] : undefined}
              guide={guideHits.length ? { hits: guideHits, menuAnimal } : undefined}
              onRetry={() => loadInfo(selected)}
              askQuestion={askQuestion}
              lens={autoTranslate}
              photoUrl={photoUrl}
              {...common}
            />
          </>
        );
      })()}

      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />
      <input ref={fileInput} type="file" accept="image/*" hidden onChange={onFile} />
    </Box>
  );
};

export default MenuScanIngredient;
